import React, { useState, useEffect } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

// Keep these imports for compatibility with the current react-intl version
import '@formatjs/intl-numberformat/polyfill'
import '@formatjs/intl-numberformat/locale-data/en'
import '@formatjs/intl-numberformat/locale-data/es'
import '@formatjs/intl-numberformat/locale-data/fr'

import '@formatjs/intl-pluralrules/polyfill'
import '@formatjs/intl-pluralrules/locale-data/en'
import '@formatjs/intl-pluralrules/locale-data/es'
import '@formatjs/intl-pluralrules/locale-data/fr'

import '@formatjs/intl-datetimeformat/polyfill'
import '@formatjs/intl-datetimeformat/locale-data/en'
import '@formatjs/intl-datetimeformat/locale-data/es'
import '@formatjs/intl-datetimeformat/locale-data/fr'

import ErrorBoundary from '../ErrorBoundary'
import PopUp from './partials/Notifications/PopUp'
import PopUpError from './partials/Notifications/PopUpError'
import DeviceAccessDenied from './partials/Notifications/DeviceAccessDenied'
import setFavicon from '../util/setFavicon'
import IntlProviderWrapper from './IntlProviderWrapper'
import Spinner from '../util/spinner-shim'

// Store
import AppDispatcher from '../dispatchers/AppDispatcher'
import getBrandName from '../util/getBrandName'
import AppStore from '../stores/AppStore'
import { useAuth } from '../util/AuthContext'

function Main () {
  const navigate = useNavigate()
  const location = useLocation()
  const { auth, logout, isAuthenticated } = useAuth()
  const [state, setState] = useState({
    data: {
      user: {
        id: '',
        customer_id: '',
        locale: {
          language: 'en',
          tempConv: 'f',
        },
        permissions: {
          level: 0,
        },
      },
    },
  })
  const [isUserDataLoaded, setIsUserDataLoaded] = useState(false)

  useEffect(() => {
    // initialize Brand based on window location
    AppDispatcher.dispatch({ action: 'get-brand', name: getBrandName() })
  }, [])

  // Effect for adding change listener (componentDidMount)
  useEffect(() => {
    AppStore.addChangeListener(onChange)

    return () => {
      AppStore.removeChangeListener(onChange)
    }
  }, [])

  // Effect to check if user data is loaded
  useEffect(() => {
    if (AppStore.data.user && AppStore.data.user.ready) {
      setIsUserDataLoaded(true)
    }
  }, [])

  const updateFavicon = () => {
    const {
      data: {
        user: { customer_id: customerID },
      },
    } = state
    const brand = AppStore.getCustomerBrand()

    const { icon_svg: iconSVG = undefined } = brand || {}
    if (iconSVG) setFavicon(iconSVG)
  }

  // Effect for handling updates (componentDidUpdate)
  useEffect(() => {
    if (isAuthenticated()) {
      const { location } = window
      const { pathname } = location

      if (
        AppStore.data.user &&
        AppStore.data.user.onboarding &&
        !pathname.match(/welcome/)
      ) {
        navigate('/welcome')
      } else if (
        AppStore.data.user &&
        !AppStore.data.user.onboarding &&
        pathname.match(/welcome/)
      ) {
        navigate('/home')
      }
      updateFavicon()
    } else {
      // If not authenticated, redirect to login
      const { location } = window
      const { pathname } = location

      // Only redirect if we're not already on the login page
      if (!pathname.match(/login|signup|terms|user\/password\/new/)) {
        navigate('/login')
      }
    }
  }, [isAuthenticated, navigate, state])

  const onChange = () => {
    updateFavicon()
    // Always provide a new reference so effect hooks run after store updates
    setState({
      data: {
        ...AppStore.data,
      },
    })
    // Mark user data as loaded when store updates
    if (AppStore.data.user && AppStore.data.user.ready) {
      setIsUserDataLoaded(true)
    }
  }

  const appDispatch = (action) => AppDispatcher.dispatch({ action })

  const closeError = () => {
    appDispatch('reset-settings-saved-status')
    appDispatch('reset-popup')
    navigate('/login')
  }

  const closeForbidden = () => {
    appDispatch('reset-popup')
    navigate('/home')
  }

  // the device access message only applies to the page it was raised for
  useEffect(() => {
    const { deviceAccessDenied } = AppStore.data.errors

    if (deviceAccessDenied && deviceAccessDenied !== location.pathname) {
      AppStore.data.errors.deviceAccessDenied = null
    }
  }, [location.pathname])

  const redirectToLogin = () => {
    logout()
    navigate('/login')
    appDispatch('reset-popup')
  }

  const { data } = state
  let languageDetect = 'en'
  if ('language' in navigator) {
    const browserLanguage = navigator.language.substring(0, 2)
    if (browserLanguage === 'es') {
      languageDetect = 'es'
    } else if (browserLanguage === 'fr') {
      languageDetect = 'fr'
    }
  }

  const language = data?.user?.locale?.language || languageDetect

  // Show loading state if user data is not yet loaded
  if (!isUserDataLoaded) {
    return (
      <div className='loading'>
        <Spinner name='double-bounce' />
      </div>
    )
  }

  let notifier
  if (data.notifications && data.notifications.popUp) {
    if (data.notifications.popUp.logout) {
      notifier = (
        <PopUpError
          key='notifier'
          text={data.notifications.popUp.text}
          visible={data.notifications.popUp.visible}
          textButton='LOGOUT'
          functionButton={redirectToLogin}
        />
      )
    } else if (data.notifications.popUp.text === 'Forbidden') {
      notifier = (
        <PopUpError
          key='notifier'
          text={data.notifications.popUp.text}
          visible={data.notifications.popUp.visible}
          textButton='OK'
          functionButton={closeForbidden}
        />
      )
    } else {
      notifier = (
        <PopUpError
          key='notifier'
          text={data.notifications.popUp.text}
          visible={data.notifications.popUp.visible}
          textButton='OK'
          functionButton={closeError}
        />
      )
    }
  }

  let savedError
  if (
    data.deviceSettings &&
    data.deviceSettings.saved &&
    data.deviceSettings.saved === 'error'
  ) {
    savedError = (
      <PopUp
        key='savedError'
        text='Error saving setting'
        visible
        textButton='OK'
        functionButton={closeError}
      />
    )
  }

  return (
    <IntlProviderWrapper locale={language}>
      <ErrorBoundary>
        <div>
          {savedError}
          {notifier}
          {/* Render the Outlet for child routes, unless the user is not allowed to see this device */}
          {data.errors?.deviceAccessDenied === location.pathname
            ? <DeviceAccessDenied onHome={() => navigate('/home')} />
            : <Outlet />}
        </div>
      </ErrorBoundary>
    </IntlProviderWrapper>
  )
}

export default Main
